/**
 * Sign-in against the backend, for the website's sign-in page. Email goes through the portal's own
 * sign-in (`/api/auth`), which answers a partner or staff address with a user session and a
 * donor-only address with a donor session; a phone number goes through the donor text sign-in
 * (`/api/donor/auth`). Either way the result is a bearer the portal completes the sign-in with.
 */
import { apiBase, portalBase } from './api';

export type Channel = 'email' | 'sms';

/** The session to hand the portal: a user session, or a donor-only one with the contact it proves. */
export interface Session {
  token: string;
  kind: 'user' | 'donor';
  contact: string;
}

export class SignInError extends Error {}

async function post<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${apiBase}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new SignInError('Could not reach the server. Please check your connection and try again.');
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) { try { data = JSON.parse(text); } catch { data = null; } }
  if (!res.ok) {
    const error = (data as { error?: unknown } | null)?.error;
    throw new SignInError(typeof error === 'string' && error ? error : `Request failed (${res.status}).`);
  }
  return data as T;
}

/** Ask for a sign-in code. The answer is neutral by design: it never says whether the address exists. */
export async function requestCode(channel: Channel, identifier: string): Promise<void> {
  if (channel === 'email') await post('/api/auth/send-otp', { email: identifier });
  else await post('/api/donor/auth/request-otp', { channel: 'sms', identifier });
}

/** Verify a code and return the session it opens. */
export async function verifyCode(channel: Channel, identifier: string, code: string): Promise<Session> {
  if (channel === 'sms') {
    const { token } = await post<{ token: string }>('/api/donor/auth/verify-otp', { channel, identifier, code });
    return { token, kind: 'donor', contact: identifier };
  }
  const { token, user, donor } = await post<{ token?: string; user?: unknown; donor?: { email: string } }>(
    '/api/auth/login',
    { email: identifier, otp: code }
  );
  if (token && user) return { token, kind: 'user', contact: identifier };
  if (token && donor) return { token, kind: 'donor', contact: donor.email };
  throw new SignInError('That code is invalid or has expired — request a new code and try again.');
}

/**
 * The portal's landing for a finished sign-in. The session rides in the URL FRAGMENT, which a browser
 * never sends to a server, so it is in no access log and no Referer.
 */
export const portalLandingFor = ({ token, kind, contact }: Session): string => {
  const fragment = new URLSearchParams({ token, kind, contact });
  return `${portalBase}/signed-in#${fragment.toString()}`;
};
